import { useLocale } from '@solinkcorp/locale';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@solinkcorp/solink-ui/components/AlertDialog';

import { Button } from '@solinkcorp/solink-ui/components/Button';
import { cn } from '@solinkcorp/solink-ui/lib/utils';

import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@solinkcorp/solink-ui/components/Dialog';

import { Form } from '@solinkcorp/solink-ui/components/ui/form';
import { useCallback, useState } from 'react';
import { toast } from 'sonner';
import { getDayPartAnalytics } from '../../../analytics/sdmAdoptionMetrics';
import { SDM_EVENTS } from '../../../constants';
import { useSDMAnalytics } from '../../../contexts/AnalyticsContext';
import { getScheduleConflictLabels, type ServiceDisplayDayPart } from '../../../day-part/utils';
import { formDaysToApi, useDayPartForm, type DayPartFormData } from '../../../hooks/useDayPartForm';
import {
  useCreateServiceDisplayDayPart,
  useUpdateServiceDisplayDayPart,
} from '../../../hooks/useServiceDisplayDayParts';
import { sdmDialogContentStyles } from '../../Monitor/LayoutSelector/LayoutSelectorStyles';
import { DayPartForm } from './DayPartForm';

interface DayPartDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** All saved day parts. */
  existingDayParts: ServiceDisplayDayPart[];
  dayPart?: ServiceDisplayDayPart;
  /** Day parts assigned to any widget across all layouts (for overlap check on update). */
  dayPartsAssignedToWidgets?: ServiceDisplayDayPart[];
  /** Map day part ID → layout names (for conflict message). */
  dayPartIdToLayoutNames?: Map<string, string[]>;
}

export const DayPartDialog = ({
  open,
  onOpenChange,
  existingDayParts,
  dayPart,
  dayPartsAssignedToWidgets = [],
  dayPartIdToLayoutNames = new Map(),
}: DayPartDialogProps) => {
  const form = useDayPartForm(existingDayParts, dayPart, open);
  const locale = useLocale();
  const analytics = useSDMAnalytics();
  const [showConfirm, setShowConfirm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const createDayPart = useCreateServiceDisplayDayPart();
  const updateDayPart = useUpdateServiceDisplayDayPart();

  const isEditing = !!dayPart;

  const watchedDays = form.watch('days');

  const getConflictLayoutNames = useCallback(
    (days: Record<string, { active: boolean; start: string; end: string }>) => {
      if (!dayPart || !dayPartsAssignedToWidgets.length) return null;
      const candidateSchedule = formDaysToApi(days ?? {});
      const others = dayPartsAssignedToWidgets.filter((dp) => dp.id !== dayPart.id);
      const conflictLabels = getScheduleConflictLabels(candidateSchedule, others);
      if (conflictLabels.length === 0) return null;
      const conflictIds = others.filter((o) => conflictLabels.includes(o.label)).map((o) => o.id);
      const layoutNameSet = new Set<string>();
      for (const id of conflictIds) {
        for (const name of dayPartIdToLayoutNames.get(id) ?? []) {
          layoutNameSet.add(name);
        }
      }
      return Array.from(layoutNameSet);
    },
    [dayPart, dayPartsAssignedToWidgets, dayPartIdToLayoutNames],
  );

  const conflictLayoutNames = getConflictLayoutNames(watchedDays ?? {});

  const submitForm = useCallback(
    async (values: DayPartFormData) => {
      const label = values.label.trim();
      if (dayPart) {
        const layoutNames = getConflictLayoutNames(values.days ?? {});
        if (layoutNames !== null && layoutNames.length > 0) {
          const conflictLocale =
            locale.serviceDisplayMonitor.dayPartDialog?.scheduleOverlapsLayoutsHelper;
          if (conflictLocale) {
            toast.error(conflictLocale.replace('{layoutNames}', layoutNames.join(', ')), {
              position: 'bottom-right',
            });
          }
          return;
        }
      }
      setIsSubmitting(true);
      try {
        const dayParts = formDaysToApi(values.days);

        if (dayPart) {
          const result = await updateDayPart(dayPart.id, { label, dayParts });

          if (!(result instanceof Error)) {
            analytics.trackSDMEvent(SDM_EVENTS.UPDATE_DAY_PART, {
              description: 'Updated a service display monitor day part',
              ...getDayPartAnalytics({ id: dayPart.id, label, dayParts }),
              existingDayPartCount: existingDayParts.length,
            });
          } else {
            throw result;
          }

          toast.success(locale.serviceDisplayMonitor.dayPartScheduleUpdated, {
            position: 'bottom-right',
          });
        } else {
          const created = await createDayPart({ label, dayParts });
          analytics.trackSDMEvent(SDM_EVENTS.CREATE_DAY_PART, {
            description: 'Created a service display monitor day part',
            ...getDayPartAnalytics({ id: created?.id, label, dayParts }),
            existingDayPartCount: existingDayParts.length + 1,
            hadNoDayPartsBefore: existingDayParts.length === 0,
          });
          toast.success(locale.serviceDisplayMonitor.dayPartScheduleCreated, {
            position: 'bottom-right',
          });
        }
        setShowConfirm(false);
        onOpenChange(false);
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Unknown error';
        const dayPartDialog = locale.serviceDisplayMonitor.dayPartDialog as
          { failedToUpdate?: string; failedToCreate?: string } | undefined;
        toast.error(
          dayPart
            ? (dayPartDialog?.failedToUpdate ?? `Failed to update day part: ${message}`)
            : (dayPartDialog?.failedToCreate ?? `Failed to create day part: ${message}`),
          { position: 'bottom-right' },
        );
      } finally {
        setIsSubmitting(false);
      }
    },
    [
      dayPart,
      createDayPart,
      updateDayPart,
      onOpenChange,
      locale,
      getConflictLayoutNames,
      analytics,
      existingDayParts.length,
    ],
  );

  const onSubmit = useCallback(
    (values: DayPartFormData) => {
      if (isEditing) {
        setShowConfirm(true);
        return;
      }
      submitForm(values);
    },
    [isEditing, submitForm],
  );

  const title = dayPart
    ? `${locale.general.edit} ${dayPart.label} ${locale.serviceDisplayMonitor.dayPartGroup}`
    : `${locale.serviceDisplayMonitor.newDayPart}`;
  const confirmLocale = locale.serviceDisplayMonitor.dayPartDialog;

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogDescription className="sr-only">
          Enter a name and time ranges for your day part.
        </DialogDescription>
        <DialogContent
          size="m"
          className={cn(sdmDialogContentStyles, 'gap-0', showConfirm && 'invisible')}
          overlayClassName={cn(showConfirm && 'invisible')}
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)}>
              <DialogHeader>
                <DialogTitle>{title}</DialogTitle>
              </DialogHeader>

              <div>
                <DayPartForm form={form} conflictLayoutNames={conflictLayoutNames} />
              </div>

              <DialogFooter>
                <DialogClose asChild>
                  <Button
                    className="cursor-pointer text-muted-foreground dark:text-white"
                    variant="outline"
                    type="button"
                  >
                    {locale.general.cancel}
                  </Button>
                </DialogClose>
                <Button
                  className="cursor-pointer text-text-inverse hover:bg-primary/90"
                  variant="default"
                  type="submit"
                  disabled={
                    isSubmitting ||
                    !!form.formState.errors.label?.message ||
                    !!form.formState.errors.days ||
                    !Object.values(form.watch('days') ?? {}).some((d) => d?.active) ||
                    conflictLayoutNames !== null
                  }
                >
                  {isSubmitting ? locale.general.saving : locale.general.save}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={showConfirm} onOpenChange={setShowConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirmLocale.confirmSaveTitle.replace('{__value__}', dayPart?.label ?? '')}
            </AlertDialogTitle>
          </AlertDialogHeader>
          <AlertDialogDescription className="text-foreground-primary body-md">
            {confirmLocale.confirmSaveDescription}
          </AlertDialogDescription>
          <AlertDialogFooter>
            <AlertDialogCancel variant="outline" size="default">
              {confirmLocale.dontSave}
            </AlertDialogCancel>
            <AlertDialogAction
              variant="default"
              size="default"
              onClick={() => void submitForm(form.getValues())}
              disabled={isSubmitting}
            >
              {isSubmitting ? locale.general.saving : locale.general.save}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
